import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaWarehouse } from 'react-icons/fa';
import api from '../../utils/api';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';

const WarehouseFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== 'new';
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  useEffect(() => {
    if (isEdit) {
      fetchWarehouse();
    }
  }, [id, isEdit]);
  
  const fetchWarehouse = async () => {
    try {
      setInitialLoading(true);
      const response = await api.get(`/inventory/warehouses/${id}`);
      const warehouse = response.data.warehouse;
      setFormData({
        name: warehouse.name || '',
        address: warehouse.address || '',
        description: warehouse.description || ''
      });
    } catch (err) {
      setError(err.response?.data?.error || '获取仓库信息失败');
    } finally {
      setInitialLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      if (isEdit) {
        await api.put(`/inventory/warehouses/${id}`, formData);
        setSuccess('仓库信息更新成功');
        setTimeout(() => {
          navigate(`/admin/inventory/warehouse/${id}`);
        }, 1500);
      } else {
        const response = await api.post('/inventory/warehouses', formData);
        setSuccess('仓库创建成功');
        setTimeout(() => {
          navigate(`/admin/inventory/warehouse/${response.data.id}`);
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || (isEdit ? '更新失败' : '创建失败'));
    } finally {
      setLoading(false);
    }
  };
  
  if (initialLoading) {
    return (
      <div className="p-6">
        <Loader />
      </div>
    );
  }
  
  return (
    <div className="warehouse-form-page">
      {/* 页面头部 */}
      <div className="mb-6">
        <Link
          to="/admin/inventory"
          className="inline-flex items-center text-gray-600 hover:text-red-700 mb-4"
        >
          <FaArrowLeft className="mr-2" />
          返回库存管理
        </Link>
        
        <h1 className="text-3xl font-bold flex items-center">
          <FaWarehouse className="mr-3 text-gray-600" />
          {isEdit ? '编辑仓库' : '新建仓库'}
        </h1>
      </div>
      
      {/* 表单 */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden max-w-2xl">
        <div className="border-b bg-gray-50 px-6 py-4">
          <h2 className="text-xl font-semibold">仓库信息</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <Message variant="error" onClose={() => setError('')}>
              {error}
            </Message>
          )}
          
          {success && (
            <Message variant="success" onClose={() => setSuccess('')}>
              {success}
            </Message>
          )}
          
          <div className="space-y-6">
            {/* 仓库名称 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                仓库名称 *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="请输入仓库名称"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                仓库名称将用于标识不同的存储位置
              </p>
            </div>
            
            {/* 仓库地址 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                仓库地址
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="请输入仓库地址"
              />
              <p className="text-sm text-gray-500 mt-1">
                详细的物理地址，便于物流和管理
              </p>
            </div>
            
            {/* 仓库描述 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                仓库描述
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="请输入仓库描述，如存储条件、特殊要求等"
              />
              <p className="text-sm text-gray-500 mt-1">
                可选的详细描述，包括仓库特点、存储条件等信息
              </p>
            </div>
          </div>
          
          {/* 表单按钮 */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/admin/inventory')}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              取消
            </button>
            
            <button
              type="submit"
              className="bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (
                <Loader size="sm" />
              ) : (
                <>
                  <FaSave className="mr-2" />
                  {isEdit ? '更新仓库' : '创建仓库'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      
      {/* 提示信息 */}
      {!isEdit && (
        <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4 max-w-2xl">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>提示：</strong>创建仓库后，您可以：
              </p>
              <ul className="mt-2 text-sm text-blue-600 list-disc list-inside">
                <li>在入库时选择该仓库作为存储位置</li>
                <li>查看每个仓库的详细库存情况</li>
                <li>在不同仓库间进行货物调拨</li>
                <li>分析各仓库的库存价值和周转情况</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseFormPage;